import React, { useState } from 'react';
import { Field } from 'redux-form';
import OptionHazardousRatesTable from './OptionHazardousRatesTable';
import { Button, Icon } from '../../../../../../common/components';
import { Col, ControlLabel, FormGroup, Row } from 'react-bootstrap';
import SchemeManagementSectionContainer
  from '../../../SchemeManagementSectionContainer/SchemeManagementSectionContainer';
import OptionAdvisoryMessage from './OptionAdvisoryMessage';
import OptionSingleRate from './OptionSingleRate';
import OptionAncillary from './OptionAncillary';
import Select from 'react-select';
import selectTheme from '../../../../../../helpers/selectTheme';
import OptionSingleRateWithMultiplier from './OptionSingleRateWithMultiplier';
import OptionRatesTable from './OptionRatesTable';

const getEmptyOption = (optionType, name, code) => {
  if (optionType === 'single_rate') {
    return {
      name: name,
      key: code,
      input: 'boolean',
      rate: 0,
      rate_type: 'fixed',
    };
  }

  if (optionType === 'haz_act') {
    return {
      key: code,
      name: name,
      options: [
        { key: 'A', rate: 0 },
        { key: 'B', rate: 0 },
        { key: 'C', rate: 0 },
        { key: 'D', rate: 0 },
      ],
      rate_type: 'loading',
    };
  }

  if (optionType === 'bolton') {
    return {
      bolt_on: true,
      commission: {
        breakdown: ['broker', 'owner'],
        type: 'difference',
      },
      input: 'boolean',
      key: code,
      name: name,
      rate_type: 'ancillary_scheme',
    };
  }

  if (optionType === 'advisory_message') {
    return {
      key: code,
      name: name,
      rate: 0,
      rate_type: 'fixed',
      auto_include: true,
      input: 'boolean',
    };
  }
};

const RemoveButton = ({ onRemove, index }) => {
  const handleRemove = () => {
    onRemove(index);
  };

  return (
    <>
      <Col sm={12}>
        <Button
          size="xs"
          handleClick={handleRemove}
          className="pull-right remove-option-button"
        >
          Remove <Icon name={'trash'} />
        </Button>
      </Col>
    </>
  );
};

const OptionMap = ({
  input,
  isViewOnly,
  onRemove,
  index,
  coverLevels
}) => {
  const hasOptions = !!input?.value?.options?.length;
  const rateType = input?.value?.rate_type;
  const key = input?.value?.key;
  const hasArguments = !!input?.value?.arguments;
  const isSingleRate = !hasArguments && !hasOptions;
  const multiplierMatch =
    typeof input.value.rate === 'string'
      ? input.value?.rate?.match(/^(\d+(?:\.\d+)?)( \* \w+)$/)
      : undefined;

  if (hasOptions) {
    return (
      <SchemeManagementSectionContainer
        subTitle={input.value.name}
        isViewOnly={isViewOnly}
      >
        <Row className={'section-item'}>
          <Field
            name={input.name}
            component={OptionHazardousRatesTable}
            isViewOnly={isViewOnly}
          />
          <RemoveButton onRemove={onRemove} index={index} />
        </Row>
      </SchemeManagementSectionContainer>
    );
  }

  if (rateType === 'ancillary_scheme') {
    return (
      <SchemeManagementSectionContainer
        subTitle={input.value.name}
        isViewOnly={isViewOnly}
      >
        <Row className={'section-item'}>
          <Field
            name={input.name}
            component={OptionAncillary}
            isViewOnly={isViewOnly}
            coverLevels={coverLevels}
          />
          <RemoveButton onRemove={onRemove} index={index} />
        </Row>
      </SchemeManagementSectionContainer>
    );
  }

  if (key === 'advisory_message') {
    return (
      <SchemeManagementSectionContainer
        subTitle={input.value.name}
        isViewOnly={isViewOnly}
      >
        <Row className={'section-item'}>
          <Field
            name={input.name}
            component={OptionAdvisoryMessage}
            isViewOnly={isViewOnly}
            coverLevels={coverLevels}
          />
          <RemoveButton onRemove={onRemove} index={index} />
        </Row>
      </SchemeManagementSectionContainer>
    );
  }

  if (isSingleRate && !!multiplierMatch) {
    const multiplier = multiplierMatch?.[2];
    const rate = multiplierMatch?.[1];

    return (
      <SchemeManagementSectionContainer
        subTitle={input.value.name}
        isViewOnly={isViewOnly}
      >
        <Row className={'section-item'}>
          <Field
            name={input.name}
            multiplier={multiplier}
            rate={rate}
            component={OptionSingleRateWithMultiplier}
            isViewOnly={isViewOnly}
          />
          <RemoveButton onRemove={onRemove} index={index} />
        </Row>
      </SchemeManagementSectionContainer>
    );
  }

  if (isSingleRate) {
    return (
      <SchemeManagementSectionContainer
        subTitle={input.value.name}
        isViewOnly={isViewOnly}
      >
        <Row className={'section-item'}>
          <Field name={input.name} component={OptionSingleRate} isViewOnly={isViewOnly} />
          <RemoveButton onRemove={onRemove} index={index} />
        </Row>
      </SchemeManagementSectionContainer>
    );
  }

  if (hasArguments) {
    return (
      <SchemeManagementSectionContainer
        subTitle={input.value.name}
        isViewOnly={isViewOnly}
      >
        <Row className={'section-item'}>
          <Field
            name={input.name}
            component={OptionRatesTable}
            isViewOnly={isViewOnly}
            coverLevels={coverLevels}
          />
          <RemoveButton onRemove={onRemove} index={index} />
        </Row>
      </SchemeManagementSectionContainer>
    );
  }

  return null;
};

const OptionsFieldArray = ({ fields, isViewOnly, optionList, coverLevels }) => {
  const [showAddOption, setShowAddOption] = useState(false);
  const [newOption, setNewOption] = useState(false);

  const handleShowAddOption = () => {
    setShowAddOption(true);
  };

  const handleNewOptionChange = (option) => {
    setNewOption(option.value);
  };

  const handleNewOptionTypeSelect = (option) => {
    const emptyOption = getEmptyOption(
      option.value,
      newOption.attributes.name,
      newOption.attributes.code,
    );

    fields.push(emptyOption);
  };

  const handleRemoveOption = (index) => {
    fields.remove(index);
  };

  return (
    <>
      {!fields.length && (
        <div>
          <h3>No Options on this scheme</h3>
        </div>
      )}
      {fields.map((field, index) => {
        return (
          <>
            <Field
              name={field}
              component={OptionMap}
              onRemove={handleRemoveOption}
              index={index}
              isViewOnly={isViewOnly}
              coverLevels={coverLevels}
            />
          </>
        );
      })}
      {!isViewOnly && (
        <Row>
          <Col sm={2}>
            <Button
              className="benefit-btn"
              bsStyle="primary"
              //size="xs"
              handleClick={handleShowAddOption}
              viewOnly={isViewOnly}
            >
              Add Option
            </Button>
          </Col>
          {showAddOption && optionList && optionList.length > 0 && (
            <Col sm={4}>
              <FormGroup>
                <Col sm={4}>
                  <ControlLabel> New Option: </ControlLabel>
                </Col>
                <Col sm={8}>
                  <Select
                    theme={selectTheme}
                    options={optionList}
                    onChange={handleNewOptionChange}
                  />
                </Col>
              </FormGroup>
            </Col>
          )}
          {newOption && (
            <Col sm={4}>
              <FormGroup>
                <Col sm={4}>
                  <ControlLabel> Option Type: </ControlLabel>
                </Col>
                <Col sm={8}>
                  <Select
                    theme={selectTheme}
                    options={[
                      { value: 'haz_act', label: 'Hazardous Activities' },
                      { value: 'single_rate', label: 'Single Rate' },
                      { value: 'bolton', label: 'Bolton' },
                      { value: 'advisory_message', label: 'Advisory Message' },
                    ]}
                    onChange={handleNewOptionTypeSelect}
                  />
                </Col>
              </FormGroup>
            </Col>
          )}
        </Row>
      )}
    </>
  );
};

export default OptionsFieldArray;
